<script setup lang="ts">
import client from "@/utils/axios";
import { errorToast, successToast } from "@/utils/custom-toast";
import { defineEmits, defineProps, ref, computed } from "vue";
import { Question } from "@/types";
import QuestionnaireModal from "@/components/Questionnaire/parts/QuestionnaireModal.vue";
import QuestionnaireProgressBar from "@/components/Questionnaire/parts/QuestionnaireProgressBar.vue";
import AbnahmeApointmentCompleted from "@/components/Questionnaire/abnahme/AbnahmeApointmentCompleted.vue";
import AbnahmeAppointmentNotCompleted from "@/components/Questionnaire/abnahme/AbnahmeAppointmentNotCompleted.vue";
import AbnahmeAppointmentFinal from "@/components/Questionnaire/abnahme/AbnahmeAppointmentFinal.vue";
import AbnahmeDataMissingTextArea from "../Questionnaire/abnahme/AbnahmeDataMissingTextArea.vue";
import { parseQcAbnahmeAnswers } from "@/utils/parse-qc-abnahme-answers";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isQcModalOpen: boolean;
  projectId: string;
  phaseId: number;
  phaseDetails: any;
};

const props = defineProps<Props>();

type Emits = {
  (event: "onClose"): void;
  (event: "onSubmit"): void;
  (event: "generateReport"): void;
};
const emits = defineEmits<Emits>();
const { t } = useTranslate();
const questions = [
  {
    slug: "qc-initial",
    modalTitle: t("questionnaire.completing_qc").value,
    question: t("questionnaire.what_is_outcome").value,
    answers: ["qc-passed", "data-missing", "qc-failed"],
    value: "",
  },
  {
    slug: "qc-passed",
    modalTitle: t("questionnaire.review_qc").value,
    answers: [],
    data: {
      ibnDate: "",
    },
    value: "",
  },
  {
    slug: "data-missing",
    modalTitle: t("questionnaire.completing_qc").value,
    answers: [],
    data: {
      description: "",
    },
    value: "",
  },
  {
    slug: "qc-failed",
    modalTitle: t("questionnaire.completing_qc").value,
    answers: [],
    data: {
      description: "",
      whatToSchedule: "",
      days: 0,
      missingMaterial: "",
      dispoNotes: "",
      fieldInstruction: "",
    },
    value: "",
  },
  {
    slug: "data-missing-review",
    modalTitle: t("questionnaire.review_qc").value,
    answers: [],
    data: null,
    value: "",
  },
  {
    slug: "qc-failed-review",
    modalTitle: t("questionnaire.review_qc").value,
    answers: [],
    data: null,
    value: "",
  },
];

const questionsState = ref<Array<Question>>([...questions]);
const isModalOpen = computed(() => props.isQcModalOpen);
const isLoading = ref(false);
const selectedQuestion = ref<Question | undefined>(
  questionsState.value
    ? questionsState.value.find((item: Question) => item.slug === "qc-initial")
    : undefined
);

const currentPhase = computed(() => {
  let currentPhaseProgress = props.phaseDetails?.phases.find(
    (phase: any) => phase.phaseId === props.phaseId
  );
  return currentPhaseProgress;
});

const questionnaireProgress = computed(() => {
  let percentage = 100;
  if (selectedQuestion.value && selectedQuestion.value) {
    switch (selectedQuestion.value.slug) {
      case "qc-initial":
        percentage = 15;
        break;
      case "appointment-not-completed":
      case "data-missing":
      case "qc-failed":
        percentage = 70;
        break;
      case "qc-passed":
      case "data-missing-review":
      case "qc-failed-review":
        percentage = 100;
        break;
    }
  }

  return percentage;
});

// QC PASSED PATH
const onQcPassed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "qc-passed"
  );
};

const onQcPassedSubmit = (ibnDate: string, generateReport?: boolean) => {
  let payload = {
    actionId: 8,
    ibnDate: ibnDate,
  };

  onSubmit(payload, generateReport);
};

const dataMissingReviewQuestions = computed(() => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    return [
      {
        question: t("questionnaire.what_data_is_missing").value,
        answer: dataMissingQuestion.data.description,
      },
    ];
  }
  return [];
});

const qcFailedReviewQuestions = computed(() => {
  let qcFailedData = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
  if (qcFailedData && qcFailedData.data) {
    let tempQuestions = parseQcAbnahmeAnswers(qcFailedData.data, "qc");
    return tempQuestions;
  }
  return [];
});

const onDataMissing = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
};
const dataMissingOnSubmit = (description: string) => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing-review"
  );
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    dataMissingQuestion.data.description = description;
  }
};

const dataMissingReviewOnSubmit = () => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    let payload = {
      actionId: 6,
      description: dataMissingQuestion.data.description,
    };

    onSubmit(payload, false);
  }
};

const onQcFailed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
};

const onQcFailedSubmit = (payload: {
  description: string;
  whatToSchedule: string;
  days: string;
  missingMaterial: string;
  dispoNotes: string;
  fieldInstruction: string;
}) => {
  let qcFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
  if (qcFailedQuestion && qcFailedQuestion.data) {
    qcFailedQuestion.data = payload;
  }
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed-review"
  );
};

const onQcFailedReviewSubmit = () => {
  let qcFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "qc-failed"
  );
  if (qcFailedQuestion && qcFailedQuestion.data) {
    let payload = {
      actionId: 7,
      metadata: qcFailedQuestion.data,
    };
    onSubmit(payload);
  }
};

const onClose = () => {
  if (questionsState.value) {
    questionsState.value = [...questions];
  }
  selectedQuestion.value = questionsState.value
    ? questionsState.value.find((item: Question) => item.slug === "qc-initial")
    : undefined;
  emits("onClose");
};

const onStepBack = (goBackToQuestionSlug: string) => {
  let backQuestion = questionsState.value.find(
    (item: Question) => item.slug === goBackToQuestionSlug
  );

  if (backQuestion) {
    selectedQuestion.value = backQuestion;
  }
};

const onSubmit = async (payload: any, generateReport?: boolean) => {
  try {
    isLoading.value = true;
    payload.isQc = true;
    let resp = await client.post(
      `/projects/${props.projectId}/phase/${props.phaseId}/complete-abnahme`,
      payload
    );

    if (resp.status === 200) {
      successToast("QC action saved.");
      if (generateReport) {
        emits("generateReport");
      }
    }
    isLoading.value = false;
    onClose();
    emits("onClose");
  } catch (error: any) {
    console.error(error);
    errorToast(error);
    isLoading.value = false;
  }
};
</script>

<template>
  <Transition name="nested">
    <div v-if="isModalOpen">
      <div
        class="w-full h-screen fixed top-0 left-0 flex items-start justify-center bg-black/75 z-[1000]"
      >
        <QuestionnaireModal class="modal" @on-close="onClose">
          <template #header>
            <div class="flex gap-2 items-center">
              <img
                class="w-4 h-4 cursor-pointer"
                src="@/assets/pencil-gray.svg"
                alt="Pencil"
              />
              <h2 class="text-xl font-semibold">
                {{ selectedQuestion ? selectedQuestion.modalTitle : "" }}
              </h2>
            </div>
          </template>
          <template #progress-bar>
            <QuestionnaireProgressBar
              :progress="questionnaireProgress"
              :warning="false"
            />
          </template>
          <template #content>
            <AbnahmeApointmentCompleted
              v-if="selectedQuestion && selectedQuestion?.slug === 'qc-initial'"
              :appointment-completed-button-text="
                t('questionnaire.qc_passed').value
              "
              :data-is-missing-button-text="
                t('questionnaire.data_is_missing').value
              "
              :apointment-failed-button-text="
                t('questionnaire.qc_failed').value
              "
              :prev-button-text="t('questionnaire.step_back').value"
              @on-abnahme-passed="onQcPassed"
              @on-data-missing="onDataMissing"
              @on-abnahme-failed="onQcFailed"
              @on-step-back="onStepBack('qc-initial')"
            />
            <AbnahmeDataMissingTextArea
              v-if="
                selectedQuestion && selectedQuestion?.slug === 'data-missing'
              "
              :textAreaTitle="t('questionnaire.what_data_is_missing').value"
              :textAreaPlaceholder="t('questionnaire.description').value"
              :prevButtonText="t('questionnaire.step_back').value"
              :nextButtonText="t('questionnaire.next').value"
              height="h-[203px]"
              :description="questionsState.find( (item: Question) =>
                  item.slug === 'data-missing')?.data.description"
              @on-submit="dataMissingOnSubmit"
              @on-step-back="onStepBack('qc-initial')"
            />
            <AbnahmeAppointmentNotCompleted
              v-if="selectedQuestion && selectedQuestion?.slug === 'qc-failed'"
              :title="t('questionnaire.qc_failed').value"
              :next-button-text="t('questionnaire.next').value"
              :prev-button-text="t('questionnaire.step_back').value"
              :text-area-placeholder-one="t('questionnaire.description').value"
              :text-area-title-one="
                t('questionnaire.write_what_happened').value
              "
              :select-menu-data-one="[
                { id: 'ac', name: 'AC' },
                { id: 'dc', name: 'DC' },
                { id: 'both', name: 'Both' },
              ]"
              :select-menu-data-one-title="
                t('questionnaire.what_do_we_schedule').value
              "
              :text-field-one-text="t('questionnaire.how_many_days').value"
              :text-field-one-placeholder="t('questionnaire.number').value"
              :text-area-title-two="
                t('questionnaire.what_material_is_missing').value
              "
              :text-area-placeholder-two="t('questionnaire.description').value"
              :text-area-title-three="t('questionnaire.dispo_notes').value"
              :text-area-placeholder-three="
                t('questionnaire.description').value
              "
              :text-area-title-four="
                t('questionnaire.what_needs_to_be_done').value
              "
              :text-area-placeholder-four="t('questionnaire.description').value"
              :text-field-one-required="true"
              :data="questionsState.find(
                (item: Question) => item.slug === 'qc-failed' )?.data"
              @on-step-back="onStepBack('qc-initial')"
              @on-submit="onQcFailedSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="
                selectedQuestion &&
                selectedQuestion?.slug === 'data-missing-review'
              "
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              :is-loading="isLoading"
              status="data-missing"
              :done-button-text="t('questionnaire.submit').value"
              :final-title="t('questionnaire.data_missing').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              :answers="dataMissingReviewQuestions"
              @on-step-back="onStepBack('data-missing')"
              @on-submit="dataMissingReviewOnSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="selectedQuestion && selectedQuestion?.slug === 'qc-passed'"
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              :is-loading="isLoading"
              :text-field-title="`IBN date ${
                currentPhase?.phaseId === 2
                  ? '(' + t('global.required').value + ')'
                  : '(' + t('global.optional').value + ')'
              }`"
              status="success"
              :done-button-text="t('questionnaire.done').value"
              :final-title="t('questionnaire.fantastic').value"
              :final-subtitle="t('questionnaire.qc_passed').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              @on-step-back="onStepBack('qc-initial')"
              @on-submit="onQcPassedSubmit"
            />
            <AbnahmeAppointmentFinal
              v-if="
                selectedQuestion &&
                selectedQuestion?.slug === 'qc-failed-review'
              "
              :current-phase="currentPhase"
              :phase-details="phaseDetails"
              :is-loading="isLoading"
              status="failed"
              :done-button-text="t('questionnaire.submit').value"
              :final-title="t('questionnaire.qc_failed').value"
              :prev-button-text="t('questionnaire.back_to_edit').value"
              :answers="qcFailedReviewQuestions"
              @on-step-back="onStepBack('qc-failed')"
              @on-submit="onQcFailedReviewSubmit"
            />
          </template>
        </QuestionnaireModal>
      </div>
    </div>
  </Transition>
</template>
